/**
 * Spec 004.5 PR1p — shared RunId / Promise registry for the
 * adapters.
 *
 * `EngineAdapter.run()` returns a `[RunId, Promise<MetricsResult>]`
 * tuple. `cancel(runId)` needs to look up the in-flight Promise
 * (more precisely: signal cancellation to the wasm module's
 * `cancel_run(numericId)` registry) and reject the Promise with
 * `CancelledError`.
 *
 * The registry here owns:
 * - the bidirectional map between the JS `RunId` (opaque string)
 *   and the wasm-side numeric `run_id: u32`,
 * - the abort signal each adapter listens on inside its `run()`
 *   body to surface mid-flight cancellation as `CancelledError`,
 * - garbage-collection of settled runs so the wasm-side u32 slot
 *   stays reusable.
 */

import { CancelledError } from "./types";

interface Entry {
  /** wasm-side u32 — passed to `cancel_run(run_id)`. */
  numericId: number;
  /** AbortController whose signal the adapter's run-body wraps. */
  abort: AbortController;
}

const entries = new Map<string, Entry>();
let nextNumericId = 1;

/**
 * Allocate a fresh run handle. Returns both the JS-facing `RunId`
 * (used by `cancel()` callers) and the wasm-side `numericId`
 * (passed verbatim into `run_webgpu` / `run_cpu`).
 */
export function allocateRun(): { runId: string; numericId: number; signal: AbortSignal } {
  const ts = Date.now().toString(36);
  const rand = Math.floor(Math.random() * 0xffff)
    .toString(36)
    .padStart(4, "0");
  const runId = `run-${ts}-${rand}-${nextNumericId.toString(16)}`;
  const numericId = nextNumericId++;
  const abort = new AbortController();
  entries.set(runId, { numericId, abort });
  return { runId, numericId, signal: abort.signal };
}

/**
 * Signal cancellation for `runId`. No-op for unknown ids per the
 * EngineAdapter contract. The abort signal fires; the adapter's
 * `run()` body sees `signal.aborted === true` and rejects with
 * `CancelledError`. The wasm side also gets the cancel via the
 * caller's `mod.cancel_run(numericId)` call (see cpu.ts/webgpu.ts).
 */
export function abortRun(runId: string): { numericId: number } | null {
  const entry = entries.get(runId);
  if (!entry) return null;
  entry.abort.abort(new CancelledError());
  return { numericId: entry.numericId };
}

/**
 * Free the registry slot after a run settles (resolve or reject).
 * Call from the adapter's `.finally()` on the underlying wasm
 * Promise to keep the map from growing across a long-lived
 * session.
 */
export function releaseRun(runId: string): void {
  entries.delete(runId);
}
